import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { t } from 'ttag';
import { RiLoader5Fill } from 'react-icons/ri';

function Button({
  alignment,
  children,
  disabled,
  icon,
  isFullWidth,
  isLoading,
  size,
  type,
  variant,
  ...rest
}) {
  const buttonClassNames = useMemo(
    () =>
      clsx(
        'border border-solid appearance-none text-left text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-deep-blue focus-visible:ring-offset-2',
        {
          'px-2 py-2 space-x-2': size === 'sm',
          'px-4 py-4 space-x-4': size === 'md'
        },
        {
          'bg-black text-white border-black enabled:hover:bg-black/80 enabled:active:bg-black/90':
            variant === 'primary',
          'bg-orange text-white border-orange enabled:hover:bg-orange/80 enabled:active:bg-orange/90':
            variant === 'secondary',
          'bg-white text-black border-gray enabled:hover:bg-light-white-smoke enabled:active:bg-white-smoke':
            variant === 'tertiary'
        },
        {
          'flex items-center justify-start': alignment === 'left',
          'flex items-center justify-center': alignment === 'center',
          'flex items-center justify-end': alignment === 'right',
          'flex items-center justify-between': alignment === 'space-between'
        },
        {
          'w-full': isFullWidth
        }
      ),
    [size, variant, alignment, isFullWidth]
  );

  const loadingClassNames = useMemo(
    () =>
      clsx('animate-spin h-4 w-4', {
        'text-white': variant === 'primary' || variant === 'secondary',
        'text-black': variant === 'tertiary'
      }),
    [variant]
  );

  return (
    <button
      className={buttonClassNames}
      type={type === 'submit' ? 'submit' : 'button'}
      disabled={disabled || isLoading}
      {...rest}
    >
      {icon && !isLoading && <span className="h-4 w-4">{icon}</span>}
      {icon && isLoading && (
        <span className={loadingClassNames}>
          <RiLoader5Fill className="h-4 w-4" />
          <span className="sr-only">{t`Loading`}</span>
        </span>
      )}
      <span>{children}</span>
    </button>
  );
}

Button.propTypes = {
  /**
   * Determines the alignment of the Button's content.
   */
  alignment: PropTypes.oneOf(['left', 'center', 'right', 'space-between']),
  /**
   * Determines the Button's text.
   */
  children: PropTypes.node.isRequired,
  /**
   * Determines whether the Button is disabled or not.
   */
  disabled: PropTypes.bool,
  /**
   * Determines the Button's icon. This will always be to the left of the text.
   */
  icon: PropTypes.node,
  /**
   * Determines if the Button should take up the full width of its container.
   */
  isFullWidth: PropTypes.bool,
  /**
   * Determines if the Button should display a loading indicator.
   */
  isLoading: PropTypes.bool,
  /**
   * Determines the Button's size.
   */
  size: PropTypes.oneOf(['sm', 'md']),
  /**
   * Determines the Button's type.
   */
  type: PropTypes.oneOf(['button', 'submit']),
  /**
   * Determines the Button's variant.
   */
  variant: PropTypes.oneOf(['primary', 'secondary', 'tertiary']).isRequired
};

Button.defaultProps = {
  alignment: 'center',
  disabled: false,
  icon: null,
  isFullWidth: false,
  isLoading: false,
  size: 'md',
  type: 'button'
};

export default Button;
